import Layout from "@/components/Layout";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, ArrowRight, Chrome, Facebook, Zap, BarChart3, Shield, Users } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "wouter";

export default function Login() {
  useEffect(() => {
    window.location.href = "https://admin.adsmanager.com.tr/giris";
  }, []);

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Redirect to admin panel
    setTimeout(() => {
      window.location.href = "https://admin.adsmanager.com.tr/giris";
    }, 1500);
  };

  const features = [
    { icon: BarChart3, text: "Gerçek zamanlı ROAS takibi" },
    { icon: Zap, text: "AI destekli bütçe optimizasyonu" },
    { icon: Users, text: "Çoklu hesap yönetimi" },
    { icon: Shield, text: "256-bit SSL güvenlik" },
  ];

  return (
    <Layout>
      <section className="bg-[#FFFDF7] lg:min-h-[85vh] flex items-center py-10 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* Left Side - Branding */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="hidden lg:block"
            >
              <div className="mb-8">
                <h1 className="text-4xl xl:text-5xl font-black text-[#0A1A3F] leading-tight mb-4">
                  Reklamlarınızı<br />
                  <span className="text-[#6B8AFE]">Tek Panelden</span><br />
                  Yönetin.
                </h1>
                <p className="text-lg text-[#0A1A3F]/60 max-w-md">
                  Meta, Google ve TikTok reklamlarınızı tek bir yerden yönetin, 
                  performansınızı artırın.
                </p>
              </div>

              {/* Feature List */}
              <div className="space-y-4">
                {features.map((feature, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-10 h-10 border-2 border-[#0A1A3F] bg-[#6B8AFE]/10 flex items-center justify-center shadow-[2px_2px_0_0_#0A1A3F]">
                      <feature.icon size={18} className="text-[#6B8AFE]" />
                    </div>
                    <span className="text-[#0A1A3F]/80 font-medium">{feature.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Stats */}
              <div className="mt-10 grid grid-cols-3 gap-4">
                {[
                  { value: "500+", label: "Aktif Marka" },
                  { value: "%340", label: "Ort. ROAS" },
                  { value: "7/24", label: "Destek" },
                ].map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + i * 0.1 }}
                    className="border-2 border-[#0A1A3F] p-3 text-center bg-white shadow-[3px_3px_0_0_#0A1A3F]"
                  >
                    <div className="text-xl font-black text-[#6B8AFE]">{stat.value}</div>
                    <div className="text-xs text-[#0A1A3F]/60 font-medium">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Side - Login Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="border-3 border-[#0A1A3F] bg-white p-6 sm:p-10 shadow-[5px_5px_0_0_#0A1A3F] sm:shadow-[6px_6px_0_0_#0A1A3F] max-w-md mx-auto lg:mx-0 lg:ml-auto">
                {/* Logo */}
                <div className="text-center mb-8">
                  <img src="/adsmanager-logo.png" alt="AdsManager" className="h-8 mx-auto mb-4" />
                  <h2 className="text-2xl font-black text-[#0A1A3F]">Giriş Yap</h2>
                  <p className="text-[#0A1A3F]/50 text-sm mt-1">Hesabınıza giriş yapın</p>
                </div>

                {/* Login Form */}
                <form onSubmit={handleLogin} className="space-y-5">
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-bold text-[#0A1A3F] mb-2">E-posta Adresi</label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A1A3F]/40" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ornek@firma.com"
                        className="w-full border-2 border-[#0A1A3F] px-10 py-3 text-[#0A1A3F] placeholder:text-[#0A1A3F]/30 focus:outline-none focus:border-[#6B8AFE] focus:shadow-[3px_3px_0_0_#6B8AFE] transition-all bg-[#FFFDF7]"
                        required
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-bold text-[#0A1A3F]">Şifre</label>
                      <a href="#" className="text-xs text-[#6B8AFE] hover:underline font-medium inline-block py-2 -my-2">Şifremi Unuttum</a>
                    </div>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A1A3F]/40" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full border-2 border-[#0A1A3F] px-10 py-3 text-[#0A1A3F] placeholder:text-[#0A1A3F]/30 focus:outline-none focus:border-[#6B8AFE] focus:shadow-[3px_3px_0_0_#6B8AFE] transition-all bg-[#FFFDF7]"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-[#0A1A3F]/40 hover:text-[#0A1A3F] transition-colors"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* Remember Me */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 border-2 border-[#0A1A3F] accent-[#6B8AFE]" />
                    <span className="text-sm text-[#0A1A3F]/60">Beni hatırla</span>
                  </label>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full border-3 border-[#0A1A3F] bg-[#6B8AFE] text-white font-bold py-3.5 px-6 shadow-[4px_4px_0_0_#0A1A3F] hover:shadow-[2px_2px_0_0_#0A1A3F] hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                        />
                        Giriş yapılıyor...
                      </div>
                    ) : (
                      <>
                        Giriş Yap
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>

                {/* Divider */}
                <div className="flex items-center gap-4 my-6">
                  <div className="flex-1 h-[2px] bg-[#0A1A3F]/10" />
                  <span className="text-xs text-[#0A1A3F]/40 font-medium">VEYA</span>
                  <div className="flex-1 h-[2px] bg-[#0A1A3F]/10" />
                </div>

                {/* Social Login */}
                <div className="grid grid-cols-2 gap-3">
                  <button className="border-2 border-[#0A1A3F] bg-white py-2.5 px-4 font-medium text-sm text-[#0A1A3F] hover:bg-[#0A1A3F]/5 transition-colors flex items-center justify-center gap-2 shadow-[2px_2px_0_0_#0A1A3F]">
                    <Chrome size={16} />
                    Google
                  </button>
                  <button className="border-2 border-[#0A1A3F] bg-white py-2.5 px-4 font-medium text-sm text-[#0A1A3F] hover:bg-[#0A1A3F]/5 transition-colors flex items-center justify-center gap-2 shadow-[2px_2px_0_0_#0A1A3F]">
                    <Facebook size={16} />
                    Facebook
                  </button>
                </div>

                {/* Register Link */}
                <p className="text-center mt-6 text-sm text-[#0A1A3F]/60">
                  Hesabınız yok mu?{" "}
                  <Link href="/facebook-reklam-hesabi-kiralama" className="text-[#6B8AFE] font-bold hover:underline inline-block py-2 -my-2">
                    Başvuru yapın
                  </Link>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
